import { notification } from "antd";
import { getInitialNotificationFilterDates } from "../Components/Notifications/NotificationsLogic";
import { NotificationType } from "../Enums/NotificationType";

import { INotification } from "../Models/Notification";
import {
    GET_NOTIFICATIONS_FAILURE,
    GET_NOTIFICATIONS_SUCCESS,
    NotificationActionType,
    SET_LOADING_NOTIFICATIONS,
    SET_NOTIFICATIONS_BY_LINK_ID_VIEWED_FAILURE,
    SET_NOTIFICATIONS_BY_LINK_ID_VIEWED_SUCCESS,
    SET_NOTIFICATIONS_DRAWER_VISIBLE,
    SET_NOTIFICATION_FILTER_DATES,
    UPDATE_ALL_NOTIFICATIONS_ACTIVE_STATUS_FAILURE,
    UPDATE_ALL_NOTIFICATIONS_ACTIVE_STATUS_SUCCESS,
    UPDATE_ALL_NOTIFICATIONS_READ_STATUS_FAILURE,
    UPDATE_ALL_NOTIFICATIONS_READ_STATUS_SUCCESS,
    UPDATE_NOTIFICATION_FAILURE,
    UPDATE_NOTIFICATION_SUCCESS,
} from "../Types/NotificationActionTypes";

export interface INotificationsReducerState {
    notifications: INotification[];
    loadingNotifications: boolean;
    drawerVisible: boolean;
    startDate: Date;
    endDate: Date;
}

const initialDates: [Date, Date] = getInitialNotificationFilterDates();

export const notificationsReducerState: INotificationsReducerState = {
    notifications: [],
    loadingNotifications: false,
    drawerVisible: false,
    startDate: initialDates[0],
    endDate: initialDates[1],
};

export const notificationsReducer = (
    state: INotificationsReducerState = { ...notificationsReducerState },
    action: NotificationActionType
): INotificationsReducerState => {
    switch (action.type) {
    /** misc */
        case SET_LOADING_NOTIFICATIONS: {
            return {
                ...state,
                loadingNotifications: action.data as boolean,
            };
        }
        case SET_NOTIFICATIONS_DRAWER_VISIBLE: {
            return {
                ...state,
                drawerVisible: action.data as boolean,
            };
        }
        case SET_NOTIFICATION_FILTER_DATES: {
            const dates = action.data as [Date, Date];
            return {
                ...state,
                startDate: dates[0],
                endDate: dates[1]
            };
        }
        /** success */
        case SET_NOTIFICATIONS_BY_LINK_ID_VIEWED_SUCCESS: {
            const readLinkId = action.data as string;
            const notificationsCopy: INotification[] = [...state.notifications];
            for (const n of notificationsCopy){
                if (n.linkId === readLinkId){
                    n.isRead = true;
                    break;
                }
            }
            return {
                ...state,
                notifications: notificationsCopy
            };
        }
        case GET_NOTIFICATIONS_SUCCESS: {
            return {
                ...state,
                notifications: action.data as INotification[],
            };
        }
        case UPDATE_ALL_NOTIFICATIONS_READ_STATUS_SUCCESS: {
            const allRead = action.data as boolean;
            const updatedNotifications: INotification[] = state.notifications.map((n: INotification) => {
                return {
                    ...n,
                    isRead: allRead
                };
            });
            return {
                ...state,
                notifications: updatedNotifications,
            };
        }
        case UPDATE_ALL_NOTIFICATIONS_ACTIVE_STATUS_SUCCESS: {
            const allActive = action.data as boolean;
            const updatedNotifications: INotification[] = state.notifications.map((n: INotification) => {
                return {
                    ...n,
                    isActive: allActive
                };
            });
            return {
                ...state,
                notifications: updatedNotifications,
            };
        }
        case UPDATE_NOTIFICATION_SUCCESS: {
            const updatedNotification = action.data as INotification;
            /** simply copy the array */
            const notificationsCopy = state.notifications.map((n: INotification) => {
                return {
                    ...n
                };
            });
            if (!updatedNotification.isActive) {
                /** if they deleted the notification, remove it from the array */
                const filteredNotifications = notificationsCopy.filter((n: INotification) => {
                    return n.id !== updatedNotification.id;
                });
                return {
                    ...state,
                    notifications: filteredNotifications
                };
            }
            /** something else was updated other than it's active status, so it still needs to show in the array */
            for (const notification of notificationsCopy) {
                if (notification.id === updatedNotification.id){
                    notification.createdDate = updatedNotification.createdDate;
                    notification.modifiedDate = updatedNotification.modifiedDate;
                    notification.description = updatedNotification.description;
                    notification.isActive = updatedNotification.isActive;
                    notification.isRead = updatedNotification.isRead;
                    notification.linkId = updatedNotification.linkId;
                    notification.notificationType = updatedNotification.notificationType;
                    notification.title = updatedNotification.title;
                    break;
                }
            }

            return {
                ...state,
                notifications: notificationsCopy
            };
        }
        /** failure */
        case SET_NOTIFICATIONS_BY_LINK_ID_VIEWED_FAILURE:
        case UPDATE_NOTIFICATION_FAILURE:
        case UPDATE_ALL_NOTIFICATIONS_ACTIVE_STATUS_FAILURE:
        case UPDATE_ALL_NOTIFICATIONS_READ_STATUS_FAILURE:
        case GET_NOTIFICATIONS_FAILURE:
        default: {
            return state;
        }
    }
};

const getMockNotifications = ():INotification[] =>{
    return [
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MatchReportComplete,
            title: "Match Report 1",
            description: "Your report is complete!",
            createdDate: new Date(new Date().setMinutes(-1)),
            modifiedDate: new Date(new Date().setMinutes(-1)),
            isRead: false,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MatchReportComplete,
            title: "Match Report 2",
            description: "Your report is complete!",
            createdDate: new Date(new Date().setMinutes(-60)),
            modifiedDate: new Date(new Date().setMinutes(-60)),
            isRead: false,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MobilyticsReportComplete,
            title: "Mobilytics Report 1",
            description: "Your report is complete!",
            createdDate: new Date(new Date().setMinutes(-120)),
            modifiedDate: new Date(new Date().setMinutes(-120)),
            isRead: true,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MatchReportError,
            title: "Match Report 1",
            description: "Your report has an error :(",
            createdDate: new Date(new Date().setHours(-24)),
            modifiedDate: new Date(new Date().setHours(-24)),
            isRead: false,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MobilyticsReportError,
            title: "Mobilytics Report 22",
            description: "Your report has an error :(",
            createdDate: new Date(new Date().setHours(-500)),
            modifiedDate: new Date(new Date().setHours(-500)),
            isRead: true,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MobilyticsReportCaching,
            title: "Mobilytics Report 23",
            description: "Caching report",
            createdDate: new Date(new Date().setHours(-5)),
            modifiedDate: new Date(new Date().setHours(-5)),
            isRead: true,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MobilyticsReportRunning,
            title: "Mobilytics Report 24",
            description: "Running Report",
            createdDate: new Date(new Date().setHours(-2)),
            modifiedDate: new Date(new Date().setHours(-2)),
            isRead: true,
            isActive: true,
        },
        {
            id: Math.random(),
            linkId: "",
            notificationType: NotificationType.MobilyticsReportPending,
            title: "Mobilytics Report 25",
            description: "Pending Report",
            createdDate: new Date(new Date().setHours(-1)),
            modifiedDate: new Date(new Date().setHours(-1)),
            isRead: true,
            isActive: true,
        },
    ];
};
